import { Akte, CalcAkte } from '@dpa/common/dist'
import { useCallback, useMemo } from 'react'

import { Section } from '@expo/html-elements'
import { Card, CardBody, CardHeader } from '../../../components/Card'

import { useAkteContext } from '../../../hooks/context/useAkteContext'

import { useDatenblattForms } from '../../../hooks/form/useDatenblattForms'
import { useUpdateAkteMutation } from '../../../hooks/update/mutations/useUpdateAkteMutation'
import { LoadingScreen } from '../../../components/LoadingScreen'
import { useUebergabeForms } from './utils'
import { UebergabeHeader } from './components/UebergabeHeader'

import { HistoryCollapsible } from './components/HistoryCollapsible'
import { useCurrentProcessGate } from './hooks/useCurrentProcessGate'
import { ProjectDecisions } from './components/ProjectDecisions'
import { ProcessUebergabeStepType } from '@dpa/common/dist/prismaGenerated/inputTypeSchemas/ProcessUebergabeStepSchema'

interface UebergabeProps {
  uebergabeTyp: ProcessUebergabeStepType
}

const nonBlockingGateFields = new Set(['PDB_Projektart'])

// Existing projects may already be in this state from the previous auto-request flow.
const editableRequestedHandoverStates = new Set(['handover_ENG2Vertrieb'])

function isEditableWorkflowState(state: string | undefined) {
  return state != undefined && (state.startsWith('workOn_') || editableRequestedHandoverStates.has(state))
}

export function Uebergabe({ uebergabeTyp }: UebergabeProps) {
  const akte = useAkteContext()

  const processGate = useCurrentProcessGate(akte, uebergabeTyp)

  const isMandatory = useCallback(
    (data: Partial<Akte>, key: keyof Akte): boolean => {
      if (processGate == undefined) return true

      const tmp = processGate[key]

      return tmp == 'required'
    },
    [processGate]
  )

  const isHidden = useCallback(
    (data: Partial<Akte>, key: keyof CalcAkte): boolean => {
      if (processGate == undefined) return true
      const tmp = processGate[key]
      return (
        tmp == 'hidden'
      )
    },
    [processGate]
  )

  const updateAkteMutation = useUpdateAkteMutation()

  const currentState = akte.stateHistory?.[0]?.state
  const isFrozen =
    !isEditableWorkflowState(currentState) ||
    akte.processUebergabeStep !== uebergabeTyp

  const { forms: datenblattForms } = useDatenblattForms(
    (data, key) => {
      return isMandatory(data, key) ? data[key] != undefined : true
    },
    isHidden,
    isFrozen,
    isMandatory
  )

  const visibleGateFields = useMemo(
    () =>
      new Set(
        Object.values(datenblattForms).flatMap(({ entries }) => entries.map(([fieldName]) => fieldName))
      ),
    [datenblattForms]
  )

  const isComplete = useMemo(() => {
    if (processGate == undefined) {
      return false
    }
    const missingFields = Object.entries(processGate).filter(
      ([fieldName, needed]) => (
        visibleGateFields.has(fieldName) &&
        !nonBlockingGateFields.has(fieldName) &&
        !(
          needed != 'required' ||
          (akte?.serverState != undefined && (akte.serverState as Record<string, unknown>)[fieldName] != undefined)
        )
      )
    )
    const isComplete = missingFields.length == 0
    return isComplete
  }, [akte.serverState, processGate, visibleGateFields])

  const forms = useUebergabeForms(datenblattForms)
  const showProjectDecisions = akte.processUebergabeStep == undefined || akte.processUebergabeStep === uebergabeTyp

  return (
    <Section key={uebergabeTyp} style={{ flex: 1 }}>
      <Card customStyle={{ flex: 1 }}>
        {(akte.isLoading || updateAkteMutation.isLoading) && (
          <CardBody customStyle={{ minHeight: 300 }}>
            <LoadingScreen active />
          </CardBody>
        )}
        {!(akte.isLoading || updateAkteMutation.isLoading) && (
          <>
            <CardHeader>
              <UebergabeHeader uebergabeStep={uebergabeTyp} uebergabeBlocked={!isComplete} />
            </CardHeader>
            {showProjectDecisions && <ProjectDecisions />}
            <CardBody customStyle={{ flex: 1, overflow: 'scroll' }}>
              {forms}
              <HistoryCollapsible identifier={akte.identifier} />
            </CardBody>
          </>
        )}
      </Card>
    </Section>
  )
}
